import { AxiosError } from 'axios';
import { startOfMonth, subMonths } from 'date-fns';
import { saveAs } from 'file-saver';
import { useState } from 'react';
import { Card, Col, Row, Toast, ToastContainer } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import SubmitButton from '../components/Buttons/SubmitButton';
import { DEFAULT_ERROR_MESSAGE } from '../consts';
import { reportsPermissions } from '../permissions';
import { RootState } from '../store/redux/store';
import { User } from '../types';
import { authApi } from '../utils/api';
import Header from '../widgets/Header';

export enum ReportTypes {
  USERS = 'users',
  PRODUCTS = 'products',
  OUTLET = 'outlet',
  STOCKADJUSTMENT = 'stock-adjustment',
  ORDERS = 'orders',
  RETURNORDERS = 'return-orders',
  INVOICES = 'invoices',
  COMMISSIONS = 'commissions',
  CREDIT = 'credit',
  FINANCIALTRANSACTIONS = 'financial-transactions'
}

interface Report {
  title: string;
  description: string;
  type: ReportTypes;
  url: string;
}

const MONTHS_INTERVAL = 3;

const END_DATE = new Date();
// Stock adjustment carries no running totals, so its window is a pure display filter:
// the beginning of the 3rd past month, e.g. on 20 Aug the range runs 1 May - 20 Aug.
const START_DATE = startOfMonth(subMonths(END_DATE, MONTHS_INTERVAL));

// The ledger epoch: the date the financial ledger was zerorized.
//
// Financial transactions are different. The backend computes cumulativeBalance and
// cumulativeCashBalance by summing over exactly the rows the requested range matches,
// so narrowing the start date silently shifts every balance in the export. This must
// stay pinned until the backend seeds the accumulation independently of the window.
const LEDGER_EPOCH = new Date('2025-09-01T13:06:30.809+00:00');

const formattedStartDate = START_DATE.toISOString();
const formattedEndDate = END_DATE.toISOString();
const formattedLedgerEpoch = LEDGER_EPOCH.toISOString();

const reports: Report[] = [
  {
    title: 'Products',
    description: 'Details of every product created on the system',
    type: ReportTypes.PRODUCTS,
    url: '/file/product'
  },
  {
    title: 'Outlets',
    description: 'Details of every outlet created on the system',
    type: ReportTypes.OUTLET,
    url: '/file/outlet'
  },
  {
    title: 'Stock Adjustment',
    description: 'Details of various stock adjustments',
    type: ReportTypes.STOCKADJUSTMENT,
    url: `/file/stock-adjustment?gte=${formattedStartDate}&lte=${formattedEndDate}`
  },
  {
    title: 'Orders',
    description: 'Comprehensive data of orders on the system',
    type: ReportTypes.ORDERS,
    url: '/file/order'
  },
  {
    title: 'Return Orders',
    description: 'Details of various return orders',
    type: ReportTypes.RETURNORDERS,
    url: '/file/return-order'
  },
  {
    title: 'Invoices',
    description: 'Comprehensive data of invoices in the system',
    type: ReportTypes.INVOICES,
    url: '/file/invoices'
  },
  {
    title: 'Commissions',
    description: 'Comprehensive data of commissions in the system',
    type: ReportTypes.COMMISSIONS,
    url: '/file/commissions'
  },
  {
    title: 'Credit',
    description: 'Details of various credit in the system',
    type: ReportTypes.CREDIT,
    url: '/file/credit'
  },
  {
    title: 'Financial Transactions',
    description: 'Details of financial transactions in the system',
    type: ReportTypes.FINANCIALTRANSACTIONS,
    url: `/file/financial-transaction?startDate=${formattedLedgerEpoch}&endDate=${formattedEndDate}`
  }
];

const Reports = () => {
  const user: User | null = useSelector((state: RootState) => state.user.currentUser);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState<{ [key in ReportTypes]: boolean }>({
    [ReportTypes.USERS]: false,
    [ReportTypes.PRODUCTS]: false,
    [ReportTypes.OUTLET]: false,
    [ReportTypes.STOCKADJUSTMENT]: false,
    [ReportTypes.ORDERS]: false,
    [ReportTypes.RETURNORDERS]: false,
    [ReportTypes.INVOICES]: false,
    [ReportTypes.COMMISSIONS]: false,
    [ReportTypes.CREDIT]: false,
    [ReportTypes.FINANCIALTRANSACTIONS]: false
  });
  const userRole = user?.role;
  const allowedReportTypes = reportsPermissions[userRole as keyof typeof reportsPermissions] || [];
  const filteredReports = reports.filter(report => allowedReportTypes.includes(report.title));
  const handleDownloadCSV = async ({ type, url }: Report) => {
    setLoading(prevLoading => ({
      ...prevLoading,
      [type]: true
    }));

    try {
      const response: BlobPart = await authApi({ method: 'get', url, params: { limit: 15000 } });
      const blob = new Blob([response], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, `${type}.csv`);

      setLoading(prevLoading => ({
        ...prevLoading,
        [type]: false
      }));
    } catch (error) {
      error instanceof AxiosError ? setError(error.response?.data.message) : setError(DEFAULT_ERROR_MESSAGE);
      setLoading(prevLoading => ({
        ...prevLoading,
        [type]: false
      }));
    }
  };

  return (
    <div className="main-content">
      <Header title="Reports" />
      <div className="container-fluid">
        <Row>
          {filteredReports.map(report => {
            const { title, description, type } = report;
            return (
              <Card className="mb-3" key={title}>
                <Card.Body>
                  <Row>
                    <Col>
                      <div>
                        <h3 className="mb-1">{title}</h3>
                        <p className="fs-4 text-muted">{description}</p>
                      </div>
                    </Col>
                    <Col className="flex-grow-0 ml-auto" style={{ justifySelf: 'end' }}>
                      <SubmitButton
                        buttonText="Download CSV"
                        loading={loading[type]}
                        style={{ minWidth: 200 }}
                        onClick={() => handleDownloadCSV(report)}
                      ></SubmitButton>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            );
          })}
        </Row>

        {error && (
          <ToastContainer position="bottom-center" className="p-3">
            <Toast bg="danger">
              <Toast.Body className="text-white">{error}</Toast.Body>
            </Toast>
          </ToastContainer>
        )}
      </div>
    </div>
  );
};

export default Reports;
